import React, { createContext, useContext, useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useDialog } from '@/common/context/DialogContext';
import type { AppointmentSlot } from '@/common/api/models/appointmentSlot';
import type { Booking } from '@/common/api/models/booking';
import type { ExamSessionResponse } from '@/common/api/models/examSessionResponse';
import { SlotStatus } from '@/common/api/models/slotStatus';

type PreviewToastFn = ReturnType<typeof useDialog>['showToastMessage'];

const PREVIEW_SLOT_DEFAULTS = {
  status: SlotStatus.available,
  lecturerId: 'preview-lecturer',
  eventKindId: 'oral-exam',
  eventKindLabel: 'Oral exam',
  boundTasks: [] as AppointmentSlot['boundTasks'],
};

const buildPreviewSlot = (
  slot: Pick<AppointmentSlot, 'slotId' | 'startTime' | 'endTime' | 'capacity' | 'availableSpots' | 'durationMinutes'>,
): AppointmentSlot => ({
  ...PREVIEW_SLOT_DEFAULTS,
  ...slot,
});

/**
 * Sample appointment slots shown in lecturer module preview (mirrors EventTaskView PreviewFlow).
 */
export const STUDENT_PREVIEW_SAMPLE_SLOTS: AppointmentSlot[] = [
  buildPreviewSlot({
    slotId: 'preview-1',
    startTime: dayjs().add(2, 'day').hour(10).minute(0)
      .second(0)
      .toISOString(),
    endTime: dayjs().add(2, 'day').hour(11).minute(0)
      .second(0)
      .toISOString(),
    capacity: 30,
    availableSpots: 24,
    durationMinutes: 60,
  }),
  buildPreviewSlot({
    slotId: 'preview-2',
    startTime: dayjs().add(5, 'day').hour(14).minute(0)
      .second(0)
      .toISOString(),
    endTime: dayjs().add(5, 'day').hour(15).minute(30)
      .second(0)
      .toISOString(),
    capacity: 30,
    availableSpots: 17,
    durationMinutes: 90,
  }),
  buildPreviewSlot({
    slotId: 'preview-3',
    startTime: dayjs().add(8, 'day').hour(9).minute(0)
      .second(0)
      .toISOString(),
    endTime: dayjs().add(8, 'day').hour(10).minute(0)
      .second(0)
      .toISOString(),
    capacity: 20,
    availableSpots: 11,
    durationMinutes: 60,
  }),
];

/**
 * What a preview mount shows instead of fetching. Omitted: the lecturer default
 * (sample slots, no booking). Visual Lab passes one per booking state.
 */
export type StudentPreviewBookingState = {
  slots: AppointmentSlot[];
  bookings: Booking[];
  examSessionStatus?: ExamSessionResponse | null;
};

const StudentPreviewSourceContext = createContext<boolean>(false);
const StudentPreviewBookingStateContext = createContext<StudentPreviewBookingState | null>(null);

/**
 * Marks descendants as lecturer student-preview mounts. Production student flows never wrap this.
 */
export const StudentPreviewSourceProvider: React.FC<{
  children: React.ReactNode;
  bookingState?: StudentPreviewBookingState;
}> = ({ children, bookingState }) => (
  <StudentPreviewSourceContext.Provider value>
    <StudentPreviewBookingStateContext.Provider value={bookingState ?? null}>
      {children}
    </StudentPreviewBookingStateContext.Provider>
  </StudentPreviewSourceContext.Provider>
);

/** True when rendered under {@link StudentPreviewSourceProvider}. */
export const useStudentPreviewSource = (): boolean => useContext(StudentPreviewSourceContext);

/** The preview's booking state, or null for the lecturer default. */
export const useStudentPreviewBookingState = (): StudentPreviewBookingState | null => (
  useContext(StudentPreviewBookingStateContext)
);

/**
 * Runs a student-only action unless lecturer preview is active; shows one toast instead.
 */
export function guardStudentAction(
  isPreviewActive: boolean,
  showToastMessage: PreviewToastFn,
  message: string,
  action: () => void | Promise<void>,
): void {
  if (isPreviewActive) {
    showToastMessage('info', message);
    return;
  }
  action();
}

/**
 * Hook that binds preview detection, dialog, and i18n for {@link guardStudentAction}.
 */
export function useGuardStudentAction(): (action: () => void | Promise<void>) => void {
  const isPreviewActive = useStudentPreviewSource();
  const { showToastMessage } = useDialog();
  const { t } = useTranslation();

  return useCallback(
    (action: () => void | Promise<void>) => {
      guardStudentAction(
        isPreviewActive,
        showToastMessage,
        t('lecturer.preview.studentActionUnavailable'),
        action,
      );
    },
    [isPreviewActive, showToastMessage, t],
  );
}
